import { ADMISSION_POLICIES } from '~/shared/utils/admissionPolicies'
import { MUSIC_TYPES } from '~/shared/utils/musicTypes'

export interface DisplayTag {
  label: string
  class: string
}

/**
 * Parade approval as a Phosphor tag: approved green, pending gold (an admin
 * still has to decide), denied red. Same colors as the approval choice in the
 * SubKrewe drawer.
 */
export function approvalTag(status: string | undefined): DisplayTag {
  switch (status) {
    case 'Yes': return { label: 'Approved', class: 'kb-tag-ok' }
    case 'No': return { label: 'Denied', class: 'kb-tag-alert' }
    default: return { label: 'Pending', class: 'kb-tag-warn' }
  }
}

/**
 * Lifecycle status, shown only when it isn't the normal "active". Abandoned
 * needs a look (gold); inactive is just a fact.
 */
export function statusTag(status: string | undefined): DisplayTag | null {
  switch (status) {
    case 'abandoned': return { label: 'Abandoned', class: 'kb-tag-warn' }
    case 'inactive': return { label: 'Inactive', class: 'kb-tag-neutral' }
    default: return null
  }
}

/**
 * Red shirt ratio severity (from useRedshirtRatio) as a tag class. Out of
 * range needs attention (gold), not an error (red); borderline is neutral.
 */
export function ratioTagClass(severity: string | undefined): string {
  switch (severity) {
    case 'success': return 'kb-tag-ok'
    case 'danger': return 'kb-tag-warn'
    default: return 'kb-tag-neutral'
  }
}

/** A table-width label for the music type; the full label goes in a tooltip. */
export function musicShortLabel(music: string | undefined): string {
  switch (music) {
    case MUSIC_TYPES.BAND: return 'Band'
    case MUSIC_TYPES.SUBKREWE_LEVEL: return 'SubKrewe'
    case MUSIC_TYPES.CROWD_LEVEL: return 'Crowd'
    case MUSIC_TYPES.BRING_BAND: return 'Brings band'
    case MUSIC_TYPES.OTHER: return 'Other'
    default: return ''
  }
}

/**
 * Admission policy as a Phosphor tag, from open to closed: green, gold,
 * orange, red. Same colors as the admission choice in the SubKrewe drawer.
 */
export function admissionTag(policy: string | undefined): DisplayTag | null {
  switch (policy) {
    case ADMISSION_POLICIES.OPEN: return { label: 'Open', class: 'kb-tag-ok' }
    case ADMISSION_POLICIES.OPEN_WITH_RESTRICTIONS: return { label: 'Open with restrictions', class: 'kb-tag-warn' }
    case ADMISSION_POLICIES.BY_REFERRAL: return { label: 'By referral', class: 'kb-tag-caution' }
    case ADMISSION_POLICIES.CLOSED: return { label: 'Closed', class: 'kb-tag-alert' }
    default: return null
  }
}

/** A table-width label for the admission policy. */
export function admissionShortLabel(policy: string | undefined): string {
  switch (policy) {
    case ADMISSION_POLICIES.OPEN: return 'Open'
    case ADMISSION_POLICIES.OPEN_WITH_RESTRICTIONS: return 'Restricted'
    case ADMISSION_POLICIES.BY_REFERRAL: return 'Referral'
    case ADMISSION_POLICIES.CLOSED: return 'Closed'
    default: return ''
  }
}
